import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { CreateFiscalYear, type ContributionCall, type FiscalYear } from '@syndiqia/ledger/contracts';
import { errorMessage } from '../../api';
import { Alert, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import {
  useAllOwnership,
  useArrears,
  useCalls,
  useCurrentYear,
  useDueToFunds,
  useFundSummaries,
  useFunds,
  useLedgerMutation,
} from '../../ledger-queries';
import { useApprovalMutation, type Approval } from '../../approvals-queries';
import { useParties, useUnits } from '../../queries';
import { useSyndicate } from '../../session';
import { FinancesNav, today } from './FinancesNav';

export function FinancesPage() {
  const { t } = useI18n();
  const syndicate = useSyndicate();
  const { year, isPending, isError, error } = useCurrentYear();

  if (!syndicate.can('ledger.read')) {
    return (
      <>
        <PageHeader title={t.finances.title} />
        <Alert tone="info">{t.common.forbidden}</Alert>
      </>
    );
  }
  return (
    <>
      <PageHeader title={t.finances.title} intro={year ? `${t.finances.year} ${year.label}` : undefined} />
      <FinancesNav />
      {isPending && <Loading label={t.loading} />}
      {isError && <Alert>{errorMessage(error, t)}</Alert>}
      {!isPending && !year && (syndicate.can('ledger.configure') ? <CreateYear /> : <Alert tone="info">{t.finances.noYear}</Alert>)}
      {year && <Overview year={year} />}
    </>
  );
}

function Overview({ year }: { year: FiscalYear }) {
  const { t, locale, money } = useI18n();
  const syndicate = useSyndicate();
  const funds = useFunds();
  const summaries = useFundSummaries(true);
  const arrears = useArrears(true);
  const calls = useCalls(year.id);
  const units = useUnits();
  const ownership = useAllOwnership();
  const parties = useParties();

  const due = calls.data?.filter((c) => !c.entryId && c.dueDate <= today()) ?? [];
  const label = (unitId: string) => units.data?.find((u) => u.id === unitId)?.label ?? '…';
  const owners = (unitId: string) =>
    ownership.data
      ?.filter((o) => o.unitId === unitId)
      .map((o) => parties.data?.find((p) => p.id === o.partyId)?.displayName ?? '…')
      .join(', ') ?? '';

  return (
    <>
      {due.length > 0 && syndicate.can('ledger.calls.post') && <PostDueCalls count={due.length} />}
      {syndicate.can('ledger.entry.post') && <TransferDueToFunds />}

      <h2>{t.finances.funds}</h2>
      {summaries.isPending && <Loading label={t.loading} />}
      <ul className="cards">
        {summaries.data?.map((s) => {
          const fund = funds.data?.find((f) => f.code === s.fundCode);
          return (
            <li key={s.fundCode} className="card card-body">
              <strong>{fund ? (locale === 'fr' ? fund.nameFr : fund.nameEn) : s.fundCode}</strong>
              <span className="figure">{money(s.netAssetsCents)}</span>
              <dl className="facts">
                <dt>{t.finances.cash}</dt>
                <dd>{money(s.cashCents)}</dd>
                <dt>{t.finances.receivable}</dt>
                <dd>{money(s.receivableCents)}</dd>
              </dl>
            </li>
          );
        })}
      </ul>

      <div className="section-head">
        <h2>{t.finances.arrears}</h2>
        {syndicate.can('collections.prepare') && <PrepareReminders />}
      </div>
      {arrears.isPending && <Loading label={t.loading} />}
      {arrears.data && arrears.data.filter((r) => r.balanceCents > 0).length === 0 && <Empty>{t.finances.noArrears}</Empty>}
      {!!arrears.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{t.finances.unit}</th>
              <th>{t.finances.owners}</th>
              <th className="num">{t.finances.balance}</th>
              <th className="num">{t.finances.d30}</th>
              <th className="num">{t.finances.d60}</th>
              <th className="num">{t.finances.d90}</th>
              <th className="num">{t.finances.over90}</th>
            </tr>
          </thead>
          <tbody>
            {arrears.data.map((r) => (
              <tr key={r.unitId} className={r.over90Cents > 0 ? 'row-alert' : undefined}>
                <td className="cell-title">
                  <Link to={`${syndicate.base}/units/${r.unitId}`}>{label(r.unitId)}</Link>
                </td>
                <td data-label={t.finances.owners}>{owners(r.unitId)}</td>
                <td className="num" data-label={t.finances.balance}>
                  {money(r.balanceCents)}
                  {r.balanceCents < 0 && <span className="muted"> ({t.finances.creditBalance})</span>}
                </td>
                <td className="num" data-label={t.finances.d30}>{r.days1to30Cents ? money(r.days1to30Cents) : ''}</td>
                <td className="num" data-label={t.finances.d60}>{r.days31to60Cents ? money(r.days31to60Cents) : ''}</td>
                <td className="num" data-label={t.finances.d90}>{r.days61to90Cents ? money(r.days61to90Cents) : ''}</td>
                <td className="num" data-label={t.finances.over90}>{r.over90Cents ? money(r.over90Cents) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function PostDueCalls({ count }: { count: number }) {
  const { t } = useI18n();
  const post = useLedgerMutation<void, ContributionCall[]>((api, base) =>
    api(`${base}/contribution-calls/post-due`, { method: 'POST', body: {} }),
  );
  return (
    <div className="callout">
      <span>{post.isSuccess ? t.finances.callsPosted(post.data.length) : t.finances.callsDue(count)}</span>
      {!post.isSuccess && (
        <button type="button" className="button button-primary" disabled={post.isPending} onClick={() => post.mutate()}>
          {t.finances.postCalls}
        </button>
      )}
      {post.isError && <Alert>{errorMessage(post.error, t)}</Alert>}
    </div>
  );
}

function TransferDueToFunds() {
  const { t, money, locale } = useI18n();
  const funds = useFunds();
  const due = useDueToFunds(true);
  const transfer = useLedgerMutation<void, unknown>((api, base) => api(`${base}/interfund-transfers`, { method: 'POST', body: {} }));
  if (!due.data?.length || transfer.isSuccess) return null;
  const name = (code: string) => {
    const f = funds.data?.find((x) => x.code === code);
    return f ? (locale === 'fr' ? f.nameFr : f.nameEn).toLowerCase() : code;
  };
  return (
    <div className="callout">
      <span>{t.bank.dueToFunds(due.data.map((d) => `${money(d.amountCents)} (${name(d.fundCode)})`).join(', '))}</span>
      <button type="button" className="button button-primary" disabled={transfer.isPending} onClick={() => transfer.mutate()}>
        {t.bank.transfer}
      </button>
      {transfer.isError && <Alert>{errorMessage(transfer.error, t)}</Alert>}
    </div>
  );
}

function PrepareReminders() {
  const { t } = useI18n();
  const s = useSyndicate();
  const navigate = useNavigate();
  const prepare = useApprovalMutation<void, { approval: Approval | null }>((api, base) => api(`${base}/collections/prepare`, { method: 'POST' }));
  return (
    <div className="inline-actions">
      <button
        type="button"
        className="button"
        disabled={prepare.isPending}
        onClick={() => prepare.mutate(undefined, { onSuccess: (r) => r.approval && navigate(`${s.base}/approvals/${r.approval.id}`) })}
      >
        {t.approvals.prepare}
      </button>
      {prepare.isSuccess && !prepare.data.approval && <span className="muted small">{t.approvals.nothing}</span>}
      {prepare.isError && <span className="field-error">{errorMessage(prepare.error, t)}</span>}
    </div>
  );
}

function CreateYear() {
  const { t } = useI18n();
  const nextYear = String(new Date().getFullYear());
  const [values, setValues] = useState({ label: nextYear, start: `${nextYear}-01-01`, end: `${Number(nextYear) + 1}-01-01` });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useLedgerMutation<CreateFiscalYear, FiscalYear>((api, base, input) =>
    api(`${base}/fiscal-years`, { method: 'POST', body: input }),
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(CreateFiscalYear, values);
    setErrors(result.errors ?? {});
    if (result.data) create.mutate(result.data);
  };
  return (
    <section className="panel">
      <p>{t.finances.noYear}</p>
      <form onSubmit={submit} className="form" noValidate>
        <div className="row">
          <Field label={t.finances.yearLabel} error={errors.label}>
            <input value={values.label} onChange={set('label')} />
          </Field>
          <Field label={t.finances.yearStart} error={errors.start}>
            <input type="date" value={values.start} onChange={set('start')} />
          </Field>
          <Field label={t.finances.yearEnd} error={errors.end}>
            <input type="date" value={values.end} onChange={set('end')} />
          </Field>
        </div>
        {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
        <div className="form-actions">
          <button type="submit" className="button button-primary" disabled={create.isPending}>
            {t.finances.createYear}
          </button>
        </div>
      </form>
    </section>
  );
}
